import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import Community from '../../types/community';
import Business from '../../types/business';
import Language, { normalizeLanguage } from '../../types/language';
import { CommunityInvite } from '../../models/inviteService.model';
import { OnboardingStep } from '../../types/misc';
import { BusinessFeatures } from '../../utils/FeatureUtils';

export type ApplicationType = {
  userId?: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: 'ADMIN' | 'USER';
  communities?: Community[];
  businesses?: Business[];
  isAuthenticated?: boolean;
  language: Language;
  onboardingStep?: OnboardingStep;

  selectedCommunity?: number;
  selectedDashboard?: number;
  selectedBusiness?: number;
};

const initialState: ApplicationType = {
  language: Language.NL,
  isAuthenticated: false,
};

const applicationSlice = createSlice({
  name: 'application',
  initialState,
  reducers: {
    login: (
      state,
      action: PayloadAction<{
        firstName: string;
        lastName: string;
        userId: number;
        email: string;
        communities?: Community[];
        businesses?: Business[];
        role?: 'ADMIN' | 'USER';
      }>,
    ): void => {
      state.isAuthenticated = true;
      state.firstName = action.payload.firstName;
      state.lastName = action.payload.lastName;
      state.userId = action.payload.userId;
      state.email = action.payload.email;
      state.communities = action.payload.communities || [];
      state.businesses = action.payload.businesses || [];
      state.role = action.payload.role || 'USER';
    },
    logout: (state): void => {
      state.isAuthenticated = false;
      state.firstName = undefined;
      state.lastName = undefined;
      state.userId = undefined;
      state.email = undefined;
      state.communities = undefined;
      state.businesses = undefined;
      state.role = undefined;
    },
    setState: (state, action: PayloadAction<ApplicationType>): void => {
      Object.assign(state, action.payload);
    },

    setPicture: (state, action: PayloadAction<string | null>): void => {
      state.communities!.find((x) => x.id === state.selectedCommunity)!.picture = action.payload;
    },

    setName: (state, action: PayloadAction<string>): void => {
      state.communities!.find((x) => x.id === state.selectedCommunity)!.name = action.payload;
    },

    setDescription: (state, action: PayloadAction<string>): void => {
      state.communities!.find((x) => x.id === state.selectedCommunity)!.description =
        action.payload;
    },

    setFeatures: (state, action: PayloadAction<Community['features']>): void => {
      state.communities!.find((x) => x.id === state.selectedCommunity)!.features = action.payload;
    },

    setMemberPermissions: (state, action: PayloadAction<Community['memberPermissions']>): void => {
      state.communities!.find((x) => x.id === state.selectedCommunity)!.memberPermissions =
        action.payload;
    },

    addCommunity: (state, action: PayloadAction<Community>): void => {
      state.communities!.push(action.payload);
    },

    deleteCommunity: (state, action: PayloadAction<number>): void => {
      state.communities = state.communities!.filter((x) => x.id !== action.payload);
    },

    setSelectedCommunity: (state, action: PayloadAction<number>): void => {
      state.selectedCommunity = action.payload;
      const community = state.communities!.find((x) => x.id === action.payload);
      state.selectedDashboard = community?.id;
    },

    wipeSelectedCommunity: (state): void => {
      state.selectedCommunity = undefined;
      state.selectedDashboard = undefined;
    },

    setSelectedDashboard: (state, action: PayloadAction<number>): void => {
      state.selectedDashboard = action.payload;
    },

    wipeSelectedDashboard: (state): void => {
      state.selectedDashboard = undefined;
    },

    addInviteFields: (state, action: PayloadAction<CommunityInvite>): void => {
      const community = state.communities!.find((x) => x.id === state.selectedCommunity);

      community!.inviteFields = action.payload;
    },

    setInviteLink: (state, action: PayloadAction<string | undefined>): void => {
      const community = state.communities!.find((x) => x.id === state.selectedCommunity);
      community!.inviteFields!.friendlyId = action.payload;
      if (action.payload) community!.inviteFields!.linkExpiry = new Date(Date.now() + 12096e5);
    },

    setLanguage: (state, action: PayloadAction<Language>): void => {
      state.language = normalizeLanguage(action.payload);
    },

    setCommunity: (state, action: PayloadAction<number | undefined>): void => {
      state.communities!.find((x) => x.id === state.selectedCommunity)!.deprecated_communityId =
        action.payload;
    },
    setOnboarding: (state, action: PayloadAction<OnboardingStep | undefined>): void => {
      state.onboardingStep = action.payload;
    },

    addBusiness: (state, action: PayloadAction<Business>): void => {
      if (!state.businesses) state.businesses = [];
      state.businesses.push(action.payload);
    },

    deleteBusiness: (state, action: PayloadAction<number>): void => {
      state.businesses = state.businesses?.filter((x) => x.businessId !== action.payload);
    },

    setBusinessPicture: (state, action: PayloadAction<string | null>): void => {
      const business = state.businesses?.find((x) => x.businessId === state.selectedBusiness);
      if (business) business.picture = action.payload;
    },

    setBusinessName: (state, action: PayloadAction<string>): void => {
      const business = state.businesses?.find((x) => x.businessId === state.selectedBusiness);
      if (business) business.name = action.payload;
    },

    setBusinessDescription: (state, action: PayloadAction<string>): void => {
      const business = state.businesses?.find((x) => x.businessId === state.selectedBusiness);
      if (business) business.description = action.payload;
    },

    setBusinessFeatures: (state, action: PayloadAction<BusinessFeatures>): void => {
      const business = state.businesses?.find((x) => x.businessId === state.selectedBusiness);
      if (business?.dashboard) {
        business.dashboard.features = action.payload;
      }
      if (business?.community) {
        business.community.features = action.payload;
        const community = state.communities?.find((c) => c.id === business.community!.id);
        if (community) {
          community.features = action.payload;
        }
      }
    },

    setSelectedBusiness: (state, action: PayloadAction<number>): void => {
      state.selectedBusiness = action.payload;
      const business = state.businesses?.find((x) => x.businessId === action.payload);
      state.selectedDashboard = business?.dashboard?.dashboardId;
    },

    wipeSelectedBusiness: (state): void => {
      state.selectedBusiness = undefined;
      state.selectedDashboard = undefined;
    },
  },
});

export const {
  setState,
  setPicture,
  setName,
  setDescription,
  setFeatures,
  setMemberPermissions,
  addCommunity,
  deleteCommunity,
  setSelectedCommunity,
  wipeSelectedCommunity,
  setSelectedDashboard,
  wipeSelectedDashboard,
  addInviteFields,
  setInviteLink,
  setLanguage,
  setCommunity,
  login,
  logout,
  setOnboarding,
  // Business actions
  addBusiness,
  deleteBusiness,
  setBusinessPicture,
  setBusinessName,
  setBusinessDescription,
  setBusinessFeatures,
  setSelectedBusiness,
  wipeSelectedBusiness,
} = applicationSlice.actions;
export default applicationSlice.reducer;
